/**
 * SyncHistoryPanel - Displays sync history and manual sync trigger
 * TASK-321/TASK-324: Collapsible panel with history table and "Sync Now" button
 *
 * Shows recent sync operations with status, timing, and record counts.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown,
  ChevronUp,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Clock,
  History,
  Download
} from 'lucide-react';
import { portfolioApiService, SyncHistoryEntry } from '../../services/api/portfolioApiService';
import { toast } from 'sonner';

interface SyncHistoryPanelProps {
  portfolioId: string;
  clientId: string;
  externalPortfolioId?: string;
  dataSource: 'manual' | 'pretim' | 'csv_import';
  syncEnabled: boolean;
  onSyncComplete?: () => void;
  className?: string;
}

const SyncHistoryPanel: React.FC<SyncHistoryPanelProps> = ({
  portfolioId,
  clientId,
  externalPortfolioId,
  dataSource,
  syncEnabled,
  onSyncComplete,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [history, setHistory] = useState<SyncHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoized fetch function to avoid recreating on each render
  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await portfolioApiService.getSyncHistory(portfolioId);
      setHistory(response.history);
    } catch {
      setError('Failed to load sync history');
    } finally {
      setIsLoading(false);
    }
  }, [portfolioId]);

  // Fetch sync history when panel expands
  useEffect(() => {
    if (isExpanded && dataSource !== 'manual') {
      fetchHistory();
    }
  }, [isExpanded, dataSource, fetchHistory]);

  const handleSyncNow = async () => {
    if (dataSource === 'manual' || !externalPortfolioId) {
      toast.error('Cannot sync manual portfolios');
      return;
    }

    setIsSyncing(true);
    try {
      const result = await portfolioApiService.syncPretim(
        clientId,
        portfolioId,
        externalPortfolioId
      );

      if (result.status === 'success') {
        toast.success(
          `Synced ${result.synced.positions_synced} positions successfully`
        );
      } else if (result.status === 'partial') {
        toast.warning(
          `Partial sync: ${result.synced.positions_synced} positions synced`
        );
      } else {
        toast.error(
          result.errors?.[0] || 'Sync failed'
        );
      }

      // Refresh history
      await fetchHistory();

      // Notify parent
      onSyncComplete?.();
    } catch {
      toast.error('Sync failed. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  // Format duration
  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    const config: Record<string, { icon: React.ElementType; color: string }> = {
      success: { icon: CheckCircle2, color: 'text-green-600' },
      partial: { icon: AlertCircle, color: 'text-yellow-600' },
      failed: { icon: XCircle, color: 'text-red-600' },
      pending: { icon: Clock, color: 'text-blue-600' },
      running: { icon: RefreshCw, color: 'text-blue-600' }
    };
    const { icon: Icon, color } = config[status] || { icon: Clock, color: 'text-gray-500' };
    return <Icon className={`w-4 h-4 ${color} ${status === 'running' ? 'animate-spin' : ''}`} />;
  };

  // Don't show for manual portfolios
  if (dataSource === 'manual') {
    return null;
  }

  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-gray-600" />
          <span className="font-medium text-gray-900">Sync History</span>
          {history.length > 0 && (
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
              {history.length} entries
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Sync Now button */}
          {syncEnabled && externalPortfolioId && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSyncNow();
              }}
              disabled={isSyncing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
          )}
          {/* Expand/collapse icon */}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-200">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-6 text-red-600">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <Download className="w-8 h-8 mb-2" />
              <p className="text-sm">No sync history yet</p>
              {syncEnabled && externalPortfolioId && (
                <p className="text-xs mt-1">Click "Sync Now" to start</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Date</th>
                    <th className="px-4 py-2 text-right">Positions</th>
                    <th className="px-4 py-2 text-right">Cash</th>
                    <th className="px-4 py-2 text-right">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(entry.status)}
                          <span className="capitalize">{entry.status}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {formatDate(entry.started_at)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className="text-green-600">{entry.positions_synced}</span>
                        {entry.positions_failed > 0 && (
                          <span className="text-red-600 ml-1">
                            / {entry.positions_failed} failed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600">
                        {entry.cash_synced || 0}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-500">
                        {formatDuration(entry.duration_ms)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Error details if last sync failed */}
          {history.length > 0 && history[0].status === 'failed' && history[0].error_message && (
            <div className="px-4 py-3 bg-red-50 border-t border-red-100">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Last sync failed</p>
                  <p className="text-xs text-red-600 mt-0.5">{history[0].error_message}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SyncHistoryPanel;
